import { Link } from "react-router-dom";
import { Clock, MapPin, Monitor, Users } from "lucide-react";
import { fmtTime, fmtDay, dateParts } from "@/lib/format";

export const DateBlock = ({ date, dark = false }) => {
  const p = dateParts(date);
  return (
    <div className={`flex w-20 shrink-0 flex-col items-center justify-center border-r py-3 ${dark ? "border-white/20 text-white" : "border-line text-brand"}`} aria-hidden="true">
      <span className="text-xs font-bold uppercase tracking-[0.14em]">{p.month}</span>
      <span className="text-4xl font-extrabold leading-none tracking-tight">{p.day}</span>
      <span className="mt-1 text-xs font-semibold opacity-70">{p.weekday}</span>
    </div>
  );
};

const timeRange = (e) => (e.end ? `${fmtTime(e.start)} – ${fmtTime(e.end)}` : fmtTime(e.start));

const SOON_MS = 3 * 24 * 60 * 60 * 1000;
export const registrationState = (e, now = new Date()) => {
  if (!e.deadline) return null;
  if (e.deadline < now) return "closed";
  return e.deadline - now <= SOON_MS ? "soon" : null;
};

export const RegistrationBadge = ({ event }) => {
  const state = event.section === "past" || event.status === "Cancelled" ? null : registrationState(event);
  if (!state) return null;
  return state === "soon" ? (
    <span className="inline-flex rounded-full bg-brand-green px-2.5 py-0.5 text-xs font-extrabold text-brand-black" data-testid={`event-reg-soon-${event.slug}`}>Registration closes soon</span>
  ) : (
    <span className="inline-flex rounded-full border border-line px-2.5 py-0.5 text-xs font-bold text-ink-muted" data-testid={`event-reg-closed-${event.slug}`}>Registration closed</span>
  );
};
const location = (e) => (e.mode === "Online" ? "Online" : `${e.venue || e.mode || "Venue to be confirmed"}${e.mode === "Hybrid" ? " · also online" : ""}`);

export const EventListItem = ({ event }) => {
  const where = location(event);
  return (
    <article className="group relative flex gap-5 border-t border-line py-6" data-testid={`home-event-${event.slug}`}>
      <DateBlock date={event.start} />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-bold uppercase tracking-[0.14em] text-ink-muted">{[event.type, event.domain].filter(Boolean).join(" · ")}</p>
        <h3 className="mt-2 text-lg font-extrabold leading-snug text-ink">
          <Link to={`/events/${event.slug}`} className="card-link group-hover:text-brand" data-testid={`home-event-link-${event.slug}`}>{event.title}</Link>
        </h3>
        <div className="mt-2"><RegistrationBadge event={event} /></div>
        <p className="sr-only">{fmtDay(event.start)}</p>
        <ul className="mt-3 space-y-1.5 text-sm text-ink-muted">
          <li className="flex items-center gap-2"><Clock className="h-4 w-4 shrink-0 text-brand" aria-hidden="true" />{timeRange(event)} IST</li>
          <li className="flex items-center gap-2">
            {event.mode === "Online" ? <Monitor className="h-4 w-4 shrink-0 text-brand" aria-hidden="true" /> : <MapPin className="h-4 w-4 shrink-0 text-brand" aria-hidden="true" />}
            {where}
          </li>
          {event.organiser && <li className="flex items-center gap-2"><Users className="h-4 w-4 shrink-0 text-brand" aria-hidden="true" />{event.organiser}</li>}
        </ul>
        <span className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-brand" aria-hidden="true">Event details <span className="transition-transform duration-200 group-hover:translate-x-1">→</span></span>
      </div>
    </article>
  );
};

export const EventCard = ({ event }) => (
  <article className="group relative flex border border-line bg-white transition-colors duration-200 hover:border-brand" data-testid={`event-card-${event.slug}`}>
    <DateBlock date={event.start} />
    <div className="min-w-0 flex-1 p-5">
      <p className="text-xs font-bold uppercase tracking-[0.14em] text-ink-muted">
        {[event.type, event.domain].filter(Boolean).join(" · ")}
      </p>
      <h3 className="mt-2 text-lg font-extrabold leading-snug text-ink">
        <Link to={`/events/${event.slug}`} className="card-link group-hover:text-brand" data-testid={`event-link-${event.slug}`}>
          {event.title}
        </Link>
      </h3>
      <div className="mt-2"><RegistrationBadge event={event} /></div>
      <p className="sr-only">{fmtDay(event.start)}</p>
      <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink-muted">
        <li className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-brand" aria-hidden="true" />{fmtTime(event.start)} IST</li>
        {event.mode && (
          <li className="flex items-center gap-1.5">
            {event.mode === "Online" ? <Monitor className="h-4 w-4 text-brand" aria-hidden="true" /> : <MapPin className="h-4 w-4 text-brand" aria-hidden="true" />}
            {event.mode}
          </li>
        )}
      </ul>
      {(event.fee || event.mood) && (
        <div className="mt-4 flex flex-wrap gap-2">
          {event.fee && <span className="chip">{event.fee}</span>}
          {event.mood && <span className="chip chip-green">{event.mood}</span>}
        </div>
      )}
    </div>
  </article>
);
